type CodeLanguage = {
  name: string;
  value: LanguageType;
  regex: RegExp;
};

/**
 * Supported languages for code in CML.
 * When you add a new language, be sure to include support for that language in the monaco editor plugin https://github.com/webedx-spark/web/blob/main/config/webpack/plugins/index.ts#L36
 *
 * @type {Array}
 */
// TODO: Generate types based on `name` and `value`
export const codeLanguages: Array<CodeLanguage> = [
  {
    name: 'JavaScript',
    value: 'javascript',
    regex: /(javascript)|(js)|(ts)|(typescript)/i,
  },

  {
    name: 'HTML',
    value: 'html',
    regex: /html/i,
  },

  {
    name: 'CSS',
    value: 'css',
    regex: /css/i,
  },

  {
    name: 'Scala',
    value: 'scala',
    regex: /scala/i,
  },

  {
    name: 'Java',
    value: 'java',
    regex: /java/i,
  },

  {
    name: 'Python',
    value: 'python',
    regex: /python/i,
  },

  {
    name: 'JSON',
    value: 'json',
    regex: /json/,
  },

  {
    name: 'PHP',
    value: 'php',
    regex: /php/i,
  },

  {
    name: 'Ruby',
    value: 'ruby',
    regex: /ruby/i,
  },

  {
    name: 'R',
    value: 'r',
    regex: /^r$/i,
  },

  {
    name: 'SQL',
    value: 'sql',
    regex: /sql/i,
  },

  {
    name: 'Perl',
    value: 'perl',
    regex: /perl/i,
  },

  {
    name: 'XML',
    value: 'xml',
    regex: /xml/i,
  },

  {
    name: 'Objective C',
    value: 'objectivec',
    regex: /(Objective C)|(objectivec)/i,
  },

  {
    name: 'Shell Script',
    value: 'sh',
    regex: /(sh)|(shell)|(script)/i,
  },

  {
    name: 'MATLAB',
    value: 'matlab',
    regex: /matlab/i,
  },

  {
    name: 'C and C++',
    value: 'c_cpp',
    regex: /(^c$)|(c\+\+)|(cpp)/i,
  },

  {
    name: 'Swift',
    value: 'swift',
    regex: /swift/i,
  },

  {
    name: 'LaTeX',
    value: 'latex',
    regex: /latex/i,
  },

  {
    name: 'Plain Text',
    value: 'plain_text',
    regex: /(plain)|(text)/i,
  },

  {
    name: 'Haskell',
    value: 'haskell',
    regex: /haskell/i,
  },

  {
    name: 'VHDL',
    value: 'vhdl',
    regex: /vhdl/i,
  },

  {
    name: 'OCaml',
    value: 'ocaml',
    regex: /ocaml/,
  },

  {
    name: 'Scheme',
    value: 'scheme',
    regex: /scheme/i,
  },

  {
    name: 'Prolog',
    value: 'prolog',
    regex: /prolog/i,
  },
  {
    name: 'Yaml',
    value: 'yaml',
    regex: /yaml/i,
  },
  {
    name: 'Dockerfile',
    value: 'dockerfile',
    regex: /docker/i,
  },
];

// Map Monaco editor language keys  to Ace Editor to language keys
export const languageMapper = {
  javascript: 'javascript',
  html: 'html',
  css: 'css',
  java: 'java',
  python: 'python',
  json: 'json',
  php: 'php',
  ruby: 'ruby',
  r: 'r',
  sql: 'sql',
  perl: 'perl',
  xml: 'xml',
  objectivec: 'objective-c',
  sh: 'shell',
  c_cpp: 'cpp', // eslint-disable-line camelcase
  swift: 'swift',
  plain_text: 'plaintext', // eslint-disable-line camelcase
  scheme: 'scheme',
  yaml: 'yaml',
  scala: 'scala',
  haskell: 'haskell',
  ocaml: 'ocaml',
  prolog: 'prolog',
  latex: 'latex',
  matlab: 'matlab',
  vhdl: 'vhdl',
  dockerfile: 'dockerfile',
} as const;

export type LanguageType = keyof typeof languageMapper;

export const isLanguageType = (val?: string): val is LanguageType =>
  !!val && codeLanguages.some((language) => language.value === val);

export default codeLanguages;
